import { useEffect, useState } from 'react'
import { Props } from './Layout'
import { useDepositAkvMutation } from './query.generated'
import { AKV__factory } from '@victgame/akiverse-ft-contracts'
import { MetamaskService } from '@/src/services/MetamaskService'
import { AkiverseLocker__factory } from '@victgame/akiverse-deposit-withdraw-contracts'
import { AKV, AKVWei, extractNumbers, weiToAKV } from '@/src/services/AKVService'
import { useLoginUser } from '../../states/LoginUser'
import { useMetamaskWalletAddress } from '../../states/MetamaskWalletAddress'

export type Params = {
  initialState: Props['state']
  onComplete: () => Promise<void>
  onClickClose: () => void
}

export const useProps = ({ initialState, onComplete, onClickClose }: Params): Props => {
  const [state, setState] = useState<Props['state']>(initialState)
  const [amount, setAmount] = useState('')
  const [depositAkvMutation] = useDepositAkvMutation()
  const [akvWalletBalance, setAkvWalletBalance] = useState<AKVWei | undefined>(undefined)
  const loginUser = useLoginUser().loginUser
  const akvBalance = loginUser?.akvBalance
  const address = loginUser?.walletAddress
  const reload = loginUser?.reload
  const currentMetamskWalletAddress = useMetamaskWalletAddress().walletAddress

  useEffect(() => {
    reload && reload()
    // 保有しているAKVを取得
    address &&
      new AKV__factory()
        .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
        .connect(MetamaskService.getSigner())
        .balanceOf(address!)
        .then((balanceOf) => setAkvWalletBalance(new AKVWei(balanceOf.toString())))
  }, [address, state, reload])

  return {
    state,
    amount,
    walletBalance: akvWalletBalance?.toAKV() ?? '-',
    inAkiverseBalance: akvBalance?.toAKV() ?? '-',
    onUpdateAmount: (value: string) => setAmount(extractNumbers(value)),
    onClickApprove: async () => {
      if (!akvWalletBalance) return
      if (!currentMetamskWalletAddress) {
        alert('Error : Please Connect to Metamask.')
        return
      }
      setState('Approving')
      try {
        // approve済みのAKVを取得
        const allowance = await new AKV__factory()
          .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
          .connect(MetamaskService.getSigner())
          .allowance(address!, process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS)
        if (allowance.lt(akvWalletBalance?.akvWei)) {
          // 足りない場合、追加でapprove
          await new AKV__factory()
            .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
            .connect(MetamaskService.getSigner())
            .approve(process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS, akvWalletBalance?.akvWei)
            .then((result) => result.wait())
        }
        setState('Deposit')
      } catch (e: any) {
        alert(e?.message || 'Error')
        setState('Approve')
      }
    },
    onClickMax: () => setAmount(Number(akvWalletBalance?.toAKV()).toFixed() ?? ''),
    onClickDeposit: async () => {
      if (!currentMetamskWalletAddress) {
        alert('Error : Please Connect to Metamask.')
        return
      }
      setState('Depositing')
      try {
        // 保有しているAKVを取得
        const balanceOf = await new AKV__factory()
          .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
          .connect(MetamaskService.getSigner())
          .balanceOf(address!)

        // 入力した値
        const inputAKV = new AKV(amount)

        if (balanceOf.lt(inputAKV.toWeiAKV())) {
          // 入力したAKV以上保有していない場合、エラー
          alert(`Error : AKV is missing, please enter less than ${weiToAKV(balanceOf)}.`)
          return
        }

        // approve済みのAKVを取得
        const allowance = await new AKV__factory()
          .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
          .connect(MetamaskService.getSigner())
          .allowance(address!, process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS)

        if (allowance.lt(inputAKV.toWeiAKV())) {
          // 足りない場合、追加でapprove
          await new AKV__factory()
            .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
            .connect(MetamaskService.getSigner())
            .approve(process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS, inputAKV.toWeiAKV().toString())
            .then((result) => result.wait())

          // approve済みのAKVを取得
          const allowance = await new AKV__factory()
            .attach(process.env.NEXT_PUBLIC_AKV_CONTRACT_ADDRESS)
            .connect(MetamaskService.getSigner())
            .allowance(address!, process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS)
          if (allowance.lt(inputAKV.toWeiAKV())) {
            // approveした値が入力した値以下の場合、エラー
            alert(`Error : AKV is missing, please enter less than ${weiToAKV(balanceOf)}.`)
            return
          }
        }

        const { hash } = await AkiverseLocker__factory.connect(
          process.env.NEXT_PUBLIC_AKIVERSE_LOCKER_ADDRESS,
          MetamaskService.getSigner(),
        ).depositAkv(inputAKV.toWeiAKV().toString())

        const result = await depositAkvMutation({
          variables: { input: { amount: inputAKV.toWeiAKV().toString(), transactionHash: hash } },
        })
        if (result.errors) {
          alert(result.errors?.[0]?.message || 'Error')
          return
        }

        await onComplete()
        setState('Deposit')
      } catch (e: any) {
        alert((e?.errors ? e.errors?.[0]?.message : e?.message) || 'Error')
        setState('Deposit')
      }
    },
    onClickClose,
  }
}
